import { motion } from "framer-motion";
import { Building2, DollarSign, Globe } from "lucide-react";
import { BilingualEyebrow, BilingualHeading } from "@/components/ui/bilingual-heading";
import { useLanguage } from "@/contexts/LanguageContext";

const BSL_IMAGES = {
  "BSL-2": "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/mkc61ir3_biosafety%20cabinet%20work%20BSL2.jpg",
  "BSL-3": "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/mskw3gu7_BSL%203.JPG",
  "BSL-4": "https://customer-assets.emergentagent.com/job_pandemic-ready/artifacts/tgavg17j_New%20BSL-4%20illustration.jpg",
};

const levelMeta = [
  { level: "BSL-2", k: "l2", color: "#10B981", rolink: true },
  { level: "BSL-3", k: "l3", color: "#FFB000", rolink: false },
  { level: "BSL-4", k: "l4", color: "#EF4444", rolink: false },
];

export default function BiosafetyInfra() {
  const { t } = useLanguage();
  const levels = levelMeta.map(l => ({
    ...l,
    desc: t(`biosafety.levels.${l.k}Desc`),
    available: t(`biosafety.levels.${l.k}Avail`),
  }));
  const rolinkLabel = t("biosafety.levels.rolinkEnabled");

  return (
    <section id="biosafety" data-testid="biosafety-section" className="py-24 md:py-32 bg-white hex-pattern">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="mb-16 max-w-2xl">
          <BilingualEyebrow en="Biosafety Infrastructure" hu="Biobiztons&aacute;gi infrastrukt&uacute;ra" className="text-[#4B5563]" />
          <BilingualHeading en="The Painful Bottleneck" hu="A kritikus sz&#369;k keresztmetszet" colorClass="text-[#111827]" />
          <p className="text-base text-[#4B5563] mt-4 leading-relaxed">
            {t("biosafety.body")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div>
            <div className="space-y-4 mb-10">
              {levels.map((l, i) => (
                <motion.div
                  key={l.level}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  data-testid={`bsl-card-${l.level.toLowerCase()}`}
                  className={`p-5 border transition-all ${l.rolink ? 'border-[#10B981]/30 bg-[#10B981]/5' : 'border-[#E5E7EB]'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full" style={{ background: l.color }} />
                      <span className="font-heading text-lg font-bold text-[#111827]">{l.level}</span>
                      {l.rolink && <span className="text-xs px-2 py-0.5 bg-[#10B981]/10 text-[#10B981] font-medium">{rolinkLabel}</span>}
                    </div>
                    <span className="text-xs text-[#4B5563] font-mono">{l.available}</span>
                  </div>
                  <p className="text-sm text-[#4B5563] leading-relaxed">{l.desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="p-6 bg-[#111827] text-white"
            >
              <p className="font-heading text-lg font-bold leading-snug">
                {t("biosafety.quotePre")}<span className="text-[#EE1D25]">{t("biosafety.quoteAccent")}</span>
              </p>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-3"
          >
            {levels.map((l, i) => (
              <motion.div
                key={l.level}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.12 }}
                className="relative overflow-hidden rounded-lg group"
              >
                <img src={BSL_IMAGES[l.level]} alt={`${l.level} laboratory`} className="w-full h-40 object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 p-4 flex items-center gap-3">
                  <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: l.color }} />
                  <span className="font-heading text-base font-bold text-white">{l.level}</span>
                  {l.rolink && <span className="text-[10px] px-1.5 py-0.5 bg-[#10B981]/20 text-[#10B981] font-medium border border-[#10B981]/30">{rolinkLabel}</span>}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
